import {
  AutocompleteInputChangeReason,
  AvatarCustomer,
  AvatarProps,
  CustomerListItem,
  FormAutocompleteField,
  FormAutocompleteFieldProps,
} from '@razz-team/ui-web';
import { useController } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomerSearchParams, useCustomerSearchQuery } from '../../api/customers';
import { ApiCustomerProfile } from '../../openapi';

export type CustomerFieldProps<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
> = Omit<
  FormAutocompleteFieldProps<string, Multiple, DisableClearable>,
  'options' | 'getOptionLabel'
>;

export function CustomerField<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
>(props: CustomerFieldProps<Multiple, DisableClearable>) {
  const { field } = useController({ name: props.name, control: props.control });

  const [inputValue, setInputValue] = useState<string>('');
  const [query, setQuery] = useState<CustomerSearchParams | undefined>(undefined);

  const [customers, setCustomers] = useState<ApiCustomerProfile[]>([]);
  const options = useMemo(() => customers.map(({ id }) => id), [customers]);

  const searchQuery = useCustomerSearchQuery(query);
  const valueQuery = useCustomerSearchQuery(
    field.value ? { type: 'id', value: field.value } : undefined,
  );
  const searchData = searchQuery.data;
  const valueData = valueQuery.data;
  useEffect(() => {
    setCustomers((prev) => {
      let result = prev;
      if (searchData && prev.findIndex(({ id }) => id === searchData.id) === -1) {
        result = [...prev, searchData];
      }
      if (valueData && prev.findIndex(({ id }) => id === valueData.id) === -1) {
        result = [...prev, valueData];
      }
      return result;
    });
  }, [searchData, valueData]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (inputValue) {
        setQuery({ type: 'username', value: inputValue });
      } else {
        setQuery(undefined);
      }
    }, 300);

    return () => clearTimeout(timerId);
  }, [inputValue]);

  const handleInputChange = useCallback(
    (_: unknown, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'input') {
        setInputValue(value);
      }
    },
    [],
  );

  const getOptionLabel = useCallback(
    (option: string) => {
      const customer = customers.find(({ id }) => id === option);
      if (customer) {
        return customer.username ? `@${customer.username}` : (customer.name ?? customer.id);
      }

      return option;
    },
    [customers],
  );

  const renderOption = useCallback(
    (optionProps: React.HTMLAttributes<HTMLLIElement>, option: string) => {
      const customer = customers.find(({ id }) => id === option);
      return customer ? (
        <CustomerListItem {...optionProps} key={option} customer={customer} />
      ) : null;
    },
    [customers],
  );

  const renderAvatar = useCallback(
    (option: string, avatarProps: Partial<AvatarProps>) => {
      const customer = customers.find(({ id }) => id === option);
      return customer ? <AvatarCustomer customer={customer} {...avatarProps} /> : null;
    },
    [customers],
  );

  return (
    <FormAutocompleteField<string, Multiple, DisableClearable>
      options={options}
      loading={searchQuery.isPending || valueQuery.isPending}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderAvatar={renderAvatar}
      onInputChange={handleInputChange}
      {...props}
    />
  );
}
